import type { ExperienceDay } from "@/lib/types";

// Prefix for day-level droppable container IDs — cannot collide with composite exp IDs
export const DAY_DROPPABLE_PREFIX = "day-drop:";

export function makeDayDropId(dayNum: number): string {
  return `${DAY_DROPPABLE_PREFIX}${dayNum}`;
}

export function makeCompositeId(dayNum: number, expId: string): string {
  return `${dayNum}:${expId}`;
}

export function parseCompositeId(id: string): { dayNum: number; expId: string } | null {
  if (id.startsWith(DAY_DROPPABLE_PREFIX)) return null; // explicitly reject day-drop IDs
  const colonIdx = id.indexOf(":");
  if (colonIdx < 0) return null;
  const dayNum = Number(id.slice(0, colonIdx));
  if (!Number.isInteger(dayNum) || dayNum <= 0) return null;
  const expId = id.slice(colonIdx + 1);
  return expId ? { dayNum, expId } : null;
}

export function reorderWithinDay(
  days: ExperienceDay[],
  dayNum: number,
  fromIdx: number,
  toIdx: number,
): ExperienceDay[] {
  if (fromIdx === toIdx) return days;
  const day = days.find(d => d.day_number === dayNum);
  if (!day) return days;
  const len = day.experiences.length;
  if (fromIdx < 0 || fromIdx >= len) return days;
  if (toIdx < 0 || toIdx >= len) return days;
  return days.map(d => {
    if (d.day_number !== dayNum) return d;
    const exps = [...d.experiences];
    const [moved] = exps.splice(fromIdx, 1);
    exps.splice(toIdx, 0, moved);
    return { ...d, experiences: exps };
  });
}

export function moveBetweenDays(
  days: ExperienceDay[],
  fromDayNum: number,
  fromIdx: number,
  toDayNum: number,
  toIdx: number,
): ExperienceDay[] {
  const fromDay = days.find(d => d.day_number === fromDayNum);
  if (!fromDay || fromIdx < 0 || fromIdx >= fromDay.experiences.length) return days;
  // Verify destination exists before removing from source
  const toDay = days.find(d => d.day_number === toDayNum);
  if (!toDay) return days;
  if (fromDayNum === toDayNum) return days;
  const item = fromDay.experiences[fromIdx];
  return days.map(day => {
    if (day.day_number === fromDayNum) {
      const exps = [...day.experiences];
      exps.splice(fromIdx, 1);
      return { ...day, experiences: exps };
    }
    if (day.day_number === toDayNum) {
      const exps = [...day.experiences];
      exps.splice(Math.min(Math.max(0, toIdx), exps.length), 0, item);
      return { ...day, experiences: exps };
    }
    return day;
  });
}

export function applyDndMove(
  days: ExperienceDay[],
  activeId: string,
  overId: string,
): ExperienceDay[] {
  const active = parseCompositeId(activeId);
  if (!active) return days;

  // Drop over a day container (including empty days)
  if (overId.startsWith(DAY_DROPPABLE_PREFIX)) {
    const toDayNum = Number(overId.slice(DAY_DROPPABLE_PREFIX.length));
    if (!Number.isInteger(toDayNum) || toDayNum <= 0) return days;
    if (toDayNum === active.dayNum) return days;
    const activeDay = days.find(d => d.day_number === active.dayNum);
    if (!activeDay) return days;
    const fromIdx = activeDay.experiences.findIndex(e => e.id === active.expId);
    if (fromIdx < 0) return days;
    const toDay = days.find(d => d.day_number === toDayNum);
    if (!toDay) return days;
    return moveBetweenDays(days, active.dayNum, fromIdx, toDayNum, toDay.experiences.length);
  }

  // Drop over another experience
  const over = parseCompositeId(overId);
  if (!over) return days;

  const activeDay = days.find(d => d.day_number === active.dayNum);
  if (!activeDay) return days;
  const fromIdx = activeDay.experiences.findIndex(e => e.id === active.expId);
  if (fromIdx < 0) return days;

  if (active.dayNum === over.dayNum) {
    const toIdx = activeDay.experiences.findIndex(e => e.id === over.expId);
    if (toIdx < 0) return days;
    return reorderWithinDay(days, active.dayNum, fromIdx, toIdx);
  }

  const overDay = days.find(d => d.day_number === over.dayNum);
  if (!overDay) return days;
  const toIdx = overDay.experiences.findIndex(e => e.id === over.expId);
  return moveBetweenDays(
    days, active.dayNum, fromIdx,
    over.dayNum, toIdx < 0 ? overDay.experiences.length : toIdx,
  );
}

export function moveUp(days: ExperienceDay[], dayNum: number, expIdx: number): ExperienceDay[] {
  const day = days.find(d => d.day_number === dayNum);
  if (!day || expIdx < 0 || expIdx >= day.experiences.length) return days;
  if (expIdx > 0) return reorderWithinDay(days, dayNum, expIdx, expIdx - 1);
  const dayIdx = days.findIndex(d => d.day_number === dayNum);
  if (dayIdx <= 0) return days;
  return moveBetweenDays(days, dayNum, 0, days[dayIdx - 1].day_number, days[dayIdx - 1].experiences.length);
}

export function moveDown(days: ExperienceDay[], dayNum: number, expIdx: number): ExperienceDay[] {
  const day = days.find(d => d.day_number === dayNum);
  if (!day || expIdx < 0 || expIdx >= day.experiences.length) return days;
  if (expIdx < day.experiences.length - 1) return reorderWithinDay(days, dayNum, expIdx, expIdx + 1);
  const dayIdx = days.findIndex(d => d.day_number === dayNum);
  if (dayIdx >= days.length - 1) return days;
  return moveBetweenDays(days, dayNum, day.experiences.length - 1, days[dayIdx + 1].day_number, 0);
}

export function moveToPrevDay(days: ExperienceDay[], dayNum: number, expIdx: number): ExperienceDay[] {
  const day = days.find(d => d.day_number === dayNum);
  if (!day || expIdx < 0 || expIdx >= day.experiences.length) return days;
  const dayIdx = days.findIndex(d => d.day_number === dayNum);
  if (dayIdx <= 0) return days;
  const prevDay = days[dayIdx - 1];
  return moveBetweenDays(days, dayNum, expIdx, prevDay.day_number, prevDay.experiences.length);
}

export function moveToNextDay(days: ExperienceDay[], dayNum: number, expIdx: number): ExperienceDay[] {
  const day = days.find(d => d.day_number === dayNum);
  if (!day || expIdx < 0 || expIdx >= day.experiences.length) return days;
  const dayIdx = days.findIndex(d => d.day_number === dayNum);
  if (dayIdx >= days.length - 1) return days;
  return moveBetweenDays(days, dayNum, expIdx, days[dayIdx + 1].day_number, 0);
}